import posthog from '@leyan/lytics';
import { notification } from 'antd';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { isNil, throttle, toLower } from 'lodash-es';
import productLine, { isAichat } from 'productLine';
import AppConfig from 'services/config/app';
import { getHealthStatus, isNimConnected } from 'services/nim/nimitt';
import AxiosClient, { Interceptors } from 'utils/AxiosClient';
import getCookie from 'utils/getCookie';
import logout from 'utils/logout';
import { doNimHealthSend } from 'services/nim/nimHealthSend';

import { demoRequestInterceptor } from './demo/interceptors';
import { getLeYanBotHost, patchClientsHost, initHostV2, switchHost } from './leyanBotHost';
import {
  isMasterOnlineDemoChannel,
  isSiteGiantDemoChannel,
  isTFTalkDemoChannel,
  isGreatChatDemoChannel,
} from 'services/chattt/channels/const';
import chatttSDK from 'services/chattt';
import { tolgee } from 'services/i18n/tolgee';
import { getUnionId } from 'utils/getUnionId';
import { authorizationEvents } from 'components/Authorization';
import { decode } from 'jsonwebtoken-esm';

const leyanbotHost = getLeYanBotHost();

export interface ImResponse<T = unknown, R = unknown> {
  code: number;
  msg: string;
  data: T;
  dataV2: R;
}

export function resolve<T>(promise: Promise<AxiosResponse<ImResponse<T>>>) {
  return promise.then((response) => {
    return response.data.data;
  });
}

let nimHealth = getHealthStatus();
const doScan = throttle(() => {
  window.postMessage({ cmd: 'start_net_scan' }, window.location.origin);
  doNimHealthSend()
    .then(() => {
      nimHealth = 'alive';
    })
    .catch(() => {
      nimHealth = 'error';
    });
}, 15 * 60 * 1000); // 允许15分钟内执行1次

const errorReport = (error: AxiosError<any>) => {
  const { response, config } = error;
  const nimConnected = isNimConnected();
  nimHealth = getHealthStatus();
  const duration = Date.now() - (response?.config as any)?.metadata?.startTime;
  // duration === 0 大概率异常
  if (error?.message === 'Network Error' || duration === 0) {
    doScan();
  }

  // 忽略304缓存的上报
  if (response?.status !== 304) {
    posthog.capture('$request_err', {
      info: {
        duration,
        status: response?.status,
        url: response?.config.url || config?.url,
        msg: response?.data?.msg || error?.message,
        nim_connected: nimConnected,
        nim_health: nimHealth,
      },
    });
  }
};

const reportReq = throttle((cb: () => void) => {
  cb();
  // @建军 在使用该事件作为活跃依据，暂时不能直接下线
  // 不过其不关注内容及指标，可以直接降低 $request 事件的上报频率
}, 5 * 60 * 60 * 1000);
export const getHeaderLang = (lang: General.SystemLanguage) =>
  lang === 'en-US' ? 'en_US' : lang === 'zh-CN' ? 'zh_CN' : lang === 'tl' ? 'ph' : lang;

const interceptors: Interceptors = {
  request: [
    async (config: AxiosRequestConfig) => {
      const ret = demoRequestInterceptor(config);
      const lang = tolgee.getLanguage();
      return {
        ...ret,
        metadata: {
          startTime: Date.now(),
        },
        headers: {
          ...ret.headers,
          lang: getHeaderLang(lang as any),
        },
      };
    },
  ],
  response: [
    (response) => {
      const { headers } = response;
      const duration = Date.now() - (response.config as any)?.metadata?.startTime;
      const url = response.config.url as string;
      const reportJson = {
        info: {
          duration,
          status: response.status,
          url,
          baseURL: response.config.baseURL,
        },
      };
      if (duration >= AppConfig.value.requestReportMinDuration) {
        posthog.capture('$request_slow', reportJson);
      }

      if (AppConfig.value.requestDurationURL?.some?.((item) => url?.includes(item))) {
        posthog.capture('$requestDuration', reportJson);
      }
      reportReq(() => {
        posthog.capture('$request', reportJson);
      });

      const newToken = headers['new-jwt'];

      if (newToken) {
        authorizationEvents.emit('authorize', newToken, decode(newToken, { json: true }), true);
      }
      return response;
    },
    (error) => {
      if (AxiosClient.isAxiosError(error)) {
        errorReport(error);
        if (error.response?.status === 401) {
          notification.error({
            duration: 0,
            // message: '未授权的访问',
            message: tolgee.t('api_error_unauthorize_msg'),
            description: tolgee.t('api_error_unauthorize_desc'),
          });

          logout('unAuthorize');
        }

        if (error.response?.status === 403) {
          window.location.hash = '/403';
        }

        if (error.response?.status === 460) {
          logout('stopService');
        }
      }

      // 网络异常，尝试切换节点
      if (error?.message?.includes?.('Network Error')) {
        trySwitchHost();
      }

      throw error;
    },
  ],
};

const deviceIdInterceptor = async (config: AxiosRequestConfig) => {
  const deviceId = await getUnionId();
  return {
    ...config,
    headers: {
      ...config.headers,
      'X-Device-Id': deviceId,
    },
  };
};

const authHeader = import.meta.env.VITE_AUTH_HEADER as string;

export const omsClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/overseaim-oms/v1`,
  },
  interceptors,
);

export const omsV2Client = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/overseaim-oms/v2`,
  },
  interceptors,
);

export const storeHomeNVClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/overseaim-store-home`,
  },
  interceptors,
);

export const storeHomeClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/overseaim-store-home/v1`,
  },
  interceptors,
);
export const storeHomeV2Client = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/overseaim-store-home/v2`,
  },
  interceptors,
);
/**
 * Basically keep asame with storeHomeClient,
 * however, skip the default interceptors,
 * in most login case the 403/401 needn't a redirect.
 * and it doesn't need a pre-auth with valid token
 */
export const loginClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/overseaim-store-home/v1`,
  },
  {
    request: [deviceIdInterceptor],
  },
);

export const loginClientV2 = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/overseaim-store-home/v2`,
  },
  {
    request: [deviceIdInterceptor],
  },
);

export const loginClientWithoutVersion = new AxiosClient({
  baseURL: `${leyanbotHost}/overseaim-store-home`,
});

export const emalaccaClient = new AxiosClient({
  baseURL: `${leyanbotHost}/overseaim-store-home/emalacca`,
});

/** 会话列表 */
export const conversationClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/oversea-door/oversea-conversation/v1`,
  },
  interceptors,
);

export const overseaPayClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/oversea-pay`,
  },
  interceptors,
);

export const leyanbotClient = new AxiosClient(
  {
    baseURL: leyanbotHost,
  },
  interceptors,
);

export const shopifyBuyerCenterClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/shopify-buyer-center`,
  },
  interceptors,
);

export const doorClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/oversea-door`,
  },
  interceptors,
);

export const userVoiceClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/oversea-user-voice`,
  },
  interceptors,
);

export const courierClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/courier`,
  },
  interceptors,
);

export const hiManagerClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/oversea-door/overseaim-hi-manager/v1`,
  },
  interceptors,
);

export const messDoorClient = new AxiosClient(
  {
    baseURL: `${leyanbotHost}/oversea-mess-door`,
  },
  interceptors,
);

/**
 * 演示版本数据都在 chatpp 产品线上
 * 因此对于 添福演示版 渠道进行特殊处理
 * 避免在 添福演示版 中无法登录的问题
 */
const productLineHeader: Oversea.ProductLine =
  isTFTalkDemoChannel ||
  isSiteGiantDemoChannel ||
  isMasterOnlineDemoChannel ||
  isAichat ||
  isGreatChatDemoChannel
    ? 'chatpp'
    : productLine;

// 需要进行 token 认证的 api 实例
const needAuthorizeClients = [
  omsClient,
  omsV2Client,
  storeHomeNVClient,
  storeHomeClient,
  storeHomeV2Client,
  conversationClient,
  leyanbotClient,
  shopifyBuyerCenterClient,
  doorClient,
  messDoorClient,
  loginClient,
  courierClient,
  hiManagerClient,
  userVoiceClient,
  overseaPayClient,
];

export function authorize(token: string) {
  needAuthorizeClients.forEach((client) => {
    client.patchConfig({
      headers: {
        ...client.getConfig().headers,
        [authHeader]: `Bearer ${token}`,
      },
    });
  });
}

const allClients = [
  ...needAuthorizeClients,
  loginClientV2,
  loginClientWithoutVersion,
  emalaccaClient,
  overseaPayClient,
];

let clientPlatform = 'web';
if (!isNil(chatttSDK)) {
  if (toLower(navigator.platform ?? '').includes('mac')) {
    clientPlatform = 'macos';
  }
  if (toLower(navigator.platform ?? '').includes('win')) {
    clientPlatform = 'windows';
  }
}

allClients.forEach((client) => {
  client.patchConfig({
    headers: {
      ...client.getConfig().headers,
      'chatpp-client-platform': clientPlatform,
      'x-lazada-ati': getCookie()._ati,
      'x-product-line': productLineHeader,
    },
  });
});

const trySwitchHost = switchHost(allClients);

initHostV2(allClients);

export const patchCurrentHost = (host: string) => {
  patchClientsHost(allClients, host);
};
